import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
} from 'react-bootstrap';
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiInfo,
    FiRefreshCw,
    FiSearch,
    FiShield,
    FiTrash2,
} from 'react-icons/fi';
import { useSelector } from 'react-redux';
import {
    executeCheckForecastBulkDelete,
    getCheckForecastBulkDeletePreview,
} from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import {
    canAccessCheckForecastBulkDelete,
    getAdminEmailFromToken,
    getBulkDeleteErrorMessage,
    getParisTodayIso,
    getPreviewCounts,
    getPreviewTotal,
    getRoleDisplayLabel,
    getTimelineCounts,
    hasBulkDeletePreviewData,
    isBulkDeleteConfirmValid,
    isHighRiskBulkDelete,
    isValidTripDay,
    formatParisDateTime,
    isPreviewFiltersMatch,
    normalizePolicyCode,
    resetBulkDeleteUiState,
    resolveAdminRole,
} from '../../utils/checkForecastBulkDelete';
import './CheckForecastBulkDeleteTab.css';

const PAGE_PREFIX = 'pages.adminTripsForecastBackupCheck.bulkDelete';

const bulkDeleteKey = (path) => PAGE_PREFIX + '.' + path;

const CountCard = ({ label, value, variant = 'default' }) => (
    <Col xs={12} sm={6} lg={3}>
        <div className={`cfbd-stat cfbd-stat--${variant}`}>
            <span className="cfbd-stat__label">{label}</span>
            <span className="cfbd-stat__value">{value ?? 0}</span>
        </div>
    </Col>
);

const PreviewSkeleton = () => (
    <Row className="g-3" aria-hidden="true">
        {[1, 2, 3, 4].map((i) => (
            <Col xs={12} sm={6} lg={3} key={i}>
                <div className="cfbd-skeleton cfbd-skeleton--stat" />
            </Col>
        ))}
        <Col xs={12}>
            <div className="cfbd-skeleton cfbd-skeleton--total" />
        </Col>
    </Row>
);

const DeletionTimeline = ({ items, tb, mode = 'preview' }) => (
    <ol className="cfbd-timeline" aria-label={tb('timeline.ariaLabel')}>
        {items.map((item, index) => (
            <li key={item.key} className="cfbd-timeline__item">
                <div className="cfbd-timeline__step">
                    <span className="cfbd-timeline__number">{item.step}</span>
                    <div className="cfbd-timeline__content">
                        <span className="cfbd-timeline__label">
                            {tb('timeline.steps.' + item.deletedKey)}
                        </span>
                        <span className="cfbd-timeline__value">
                            {mode === 'preview'
                                ? tb('timeline.toDelete', { count: item.value })
                                : tb('timeline.deleted', { count: item.value })}
                        </span>
                    </div>
                </div>
                {index < items.length - 1 && (
                    <span className="cfbd-timeline__arrow" aria-hidden="true">↓</span>
                )}
            </li>
        ))}
    </ol>
);

const CheckForecastBulkDeleteTab = ({ mode = 'byDate', showHeader = true }) => {
    const { t } = useTranslation();
    const tb = (path, params) => t(bulkDeleteKey(path), params);
    const accessToken = useSelector((state) => state.adminAuth?.accessToken);
    const role = resolveAdminRole(accessToken);
    const canAccess = canAccessCheckForecastBulkDelete(role);
    const roleLabel = getRoleDisplayLabel(role);
    const userEmail = getAdminEmailFromToken(accessToken);

    const [tripDay, setTripDay] = useState(() => getParisTodayIso());
    const [policyCode, setPolicyCode] = useState('');
    const [preview, setPreview] = useState(null);
    const [previewTripDay, setPreviewTripDay] = useState('');
    const [previewPolicyCode, setPreviewPolicyCode] = useState('');
    const [deleteResult, setDeleteResult] = useState(null);
    const [deletedAt, setDeletedAt] = useState(null);
    const [deletedPolicyCode, setDeletedPolicyCode] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmChecked, setConfirmChecked] = useState(false);
    const [confirmText, setConfirmText] = useState('');

    const requestVersionRef = useRef(0);
    const abortRef = useRef(null);
    const confirmInputRef = useRef(null);
    const dateInputRef = useRef(null);

    useEffect(() => () => abortRef.current?.abort(), []);

    useEffect(() => {
        if (!deleteLoading) return undefined;
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [deleteLoading]);

    useEffect(() => {
        if (!showConfirm) return;
        const timer = window.setTimeout(() => {
            confirmInputRef.current?.focus();
        }, 100);
        return () => window.clearTimeout(timer);
    }, [showConfirm]);

    const isPolicyRequired = mode === 'byDateAndPolicy';
    const modeKey = isPolicyRequired ? 'byDatePolicy' : 'byDate';

    const errorLabels = {
        invalidDate: tb('errors.invalidDate'),
        policyCodeRequired: tb('errors.policyCodeRequired'),
        unauthorized: tb('errors.unauthorized'),
        forbidden: tb('errors.forbidden'),
        server: tb('errors.server'),
        default: tb('errors.default'),
    };

    const invalidateRequest = () => {
        requestVersionRef.current += 1;
        abortRef.current?.abort();
        abortRef.current = null;
    };

    const resetConfirmState = () => {
        setConfirmChecked(false);
        setConfirmText('');
    };

    const resetPreviewState = () => {
        const reset = resetBulkDeleteUiState({
            tripDay,
            policyCode,
            preview,
            previewTripDay,
            previewPolicyCode,
            deleteResult,
            previewError,
            deleteError,
            showConfirm,
            confirmChecked,
            confirmText,
        });
        setPreview(reset.preview);
        setPreviewTripDay(reset.previewTripDay);
        setPreviewPolicyCode(reset.previewPolicyCode);
        setDeleteResult(reset.deleteResult);
        setDeletedAt(null);
        setDeletedPolicyCode('');
        setPreviewError(reset.previewError);
        setDeleteError(reset.deleteError);
        setShowConfirm(reset.showConfirm);
        setConfirmChecked(reset.confirmChecked);
        setConfirmText(reset.confirmText);
    };

    const handleTripDayChange = (value) => {
        invalidateRequest();
        setPreviewLoading(false);
        setDeleteLoading(false);
        setTripDay(value);
        resetPreviewState();
    };

    const handlePolicyCodeChange = (value) => {
        invalidateRequest();
        setPreviewLoading(false);
        setDeleteLoading(false);
        setPolicyCode(value);
        resetPreviewState();
    };

    const handlePreview = async (event) => {
        event?.preventDefault();
        const trimmed = tripDay.trim();
        if (!isValidTripDay(trimmed)) {
            setPreviewError(tb('errors.invalidDate'));
            dateInputRef.current?.focus();
            return;
        }

        const trimmedPolicyCode = isPolicyRequired ? normalizePolicyCode(policyCode) : '';
        if (isPolicyRequired && !trimmedPolicyCode) {
            setPreviewError(tb('errors.policyCodeRequired'));
            return;
        }

        invalidateRequest();
        const version = requestVersionRef.current;
        const controller = new AbortController();
        abortRef.current = controller;

        setPreviewLoading(true);
        setPreviewError(null);
        setDeleteError(null);
        setDeleteResult(null);
        setDeletedAt(null);
        setDeletedPolicyCode('');
        setPreview(null);
        setPreviewTripDay('');
        setPreviewPolicyCode('');
        setShowConfirm(false);
        resetConfirmState();

        try {
            const response = await getCheckForecastBulkDeletePreview({
                tripDay: trimmed,
                policyCode: trimmedPolicyCode,
                signal: controller.signal,
            });
            if (version !== requestVersionRef.current) return;
            setPreview(response);
            setPreviewTripDay(trimmed);
            setPreviewPolicyCode(trimmedPolicyCode);
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
            if (version !== requestVersionRef.current) return;
            setPreviewError(getBulkDeleteErrorMessage(err, errorLabels));
        } finally {
            if (version === requestVersionRef.current) {
                setPreviewLoading(false);
                abortRef.current = null;
            }
        }
    };

    const handleOpenConfirm = () => {
        if (!hasBulkDeletePreviewData(preview)) return;
        if (!isPreviewFiltersMatch({
            tripDay,
            policyCode,
            previewTripDay,
            previewPolicyCode,
        })) return;
        resetConfirmState();
        setShowConfirm(true);
    };

    const previewTotal = getPreviewTotal(preview);
    const isHighRisk = isHighRiskBulkDelete(previewTotal);
    const isConfirmValid = isBulkDeleteConfirmValid({
        confirmChecked,
        confirmText,
        tripDay: previewTripDay,
        total: previewTotal,
    });

    const handleConfirmDelete = async () => {
        const trimmed = previewTripDay.trim();
        if (!isConfirmValid || !isValidTripDay(trimmed) || !hasBulkDeletePreviewData(preview)) return;

        invalidateRequest();
        const version = requestVersionRef.current;
        const controller = new AbortController();
        abortRef.current = controller;

        setDeleteLoading(true);
        setDeleteError(null);

        try {
            const response = await executeCheckForecastBulkDelete({
                tripDay: trimmed,
                policyCode: previewPolicyCode,
                signal: controller.signal,
            });
            if (version !== requestVersionRef.current) return;

            if (response?.success === false) {
                setDeleteError(response.message || tb('errors.confirmRequired'));
                return;
            }

            setDeleteResult(response);
            setDeletedAt(new Date());
            setDeletedPolicyCode(previewPolicyCode);
            setPreview(null);
            setPreviewTripDay('');
            setPreviewPolicyCode('');
            setShowConfirm(false);
            resetConfirmState();
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
            if (version !== requestVersionRef.current) return;
            setDeleteError(getBulkDeleteErrorMessage(err, errorLabels));
        } finally {
            if (version === requestVersionRef.current) {
                setDeleteLoading(false);
                abortRef.current = null;
            }
        }
    };

    const focusDateInput = () => {
        dateInputRef.current?.focus();
    };

    const previewReady = Boolean(
        preview
        && isPreviewFiltersMatch({
            tripDay,
            policyCode,
            previewTripDay,
            previewPolicyCode,
        })
        && !previewLoading,
    );
    const previewedPolicyCode = normalizePolicyCode(previewPolicyCode);
    const resultPolicyCode = normalizePolicyCode(
        deleteResult?.policy_code || deletedPolicyCode,
    );
    const previewHasData = previewReady && hasBulkDeletePreviewData(preview);
    const previewEmpty = previewReady && !hasBulkDeletePreviewData(preview);
    const isBusy = previewLoading || deleteLoading;

    if (!canAccess) {
        if (!showHeader) return null;
        return (
            <Alert variant="warning" className="mb-0" role="alert">
                {tb('forbidden')}
            </Alert>
        );
    }

    const fieldIdPrefix = 'cfbd-' + mode;

    return (
        <div className={showHeader ? 'cfbd-panel' : 'cfbd-mode-panel'}>
            {showHeader && (
                <section className="cfbd-section cfbd-section--header" aria-labelledby="cfbd-section-1-title">
                    <div className="cfbd-section__head">
                        <div>
                            <h3 id="cfbd-section-1-title" className="cfbd-panel__title">
                                <FiTrash2 size={22} aria-hidden />
                                {tb('title')}
                            </h3>
                            <p className="cfbd-panel__desc">{tb('description')}</p>
                        </div>
                        <Badge bg="primary" className="cfbd-permission-badge" aria-label={tb('permissionBadgeAria', { role: roleLabel })}>
                            <FiShield size={14} className="me-1" aria-hidden />
                            {tb('permissionBadge', { role: roleLabel })}
                        </Badge>
                    </div>
                </section>
            )}

            <section className="cfbd-section cfbd-section--form" aria-labelledby={fieldIdPrefix + '-section-title'}>
                <div className="cfbd-mode-header">
                    <h4 id={fieldIdPrefix + '-section-title'} className="cfbd-section__title">
                        {tb('modes.' + modeKey + '.title')}
                    </h4>
                    <p className="cfbd-mode-desc">{tb('modes.' + modeKey + '.description')}</p>
                </div>
                <Form onSubmit={handlePreview} className="cfbd-form">
                    <div className="cfbd-form-toolbar">
                        <Form.Group className="cfbd-form-field cfbd-form-field--date">
                            <Form.Label htmlFor={fieldIdPrefix + '-trip-day'}>
                                {tb('tripDayLabel')}
                            </Form.Label>
                            <Form.Control
                                ref={dateInputRef}
                                id={fieldIdPrefix + '-trip-day'}
                                type="date"
                                value={tripDay}
                                onChange={(e) => handleTripDayChange(e.target.value)}
                                disabled={isBusy}
                                required
                                aria-describedby={fieldIdPrefix + '-form-hint'}
                            />
                        </Form.Group>
                        {isPolicyRequired && (
                            <Form.Group className="cfbd-form-field cfbd-form-field--policy">
                                <Form.Label htmlFor={fieldIdPrefix + '-policy-code'}>
                                    {tb('policyCodeLabel')}
                                    <span className="text-danger ms-1">*</span>
                                </Form.Label>
                                <Form.Control
                                    id={fieldIdPrefix + '-policy-code'}
                                    type="text"
                                    value={policyCode}
                                    onChange={(e) => handlePolicyCodeChange(e.target.value)}
                                    disabled={isBusy}
                                    placeholder={tb('policyCodePlaceholder')}
                                    aria-describedby={fieldIdPrefix + '-form-hint'}
                                    autoComplete="off"
                                    required
                                />
                            </Form.Group>
                        )}
                        <div className="cfbd-form-action">
                            <Button
                                type="submit"
                                className="btn fb-btn-primary cfbd-btn-primary"
                                disabled={isBusy || !tripDay.trim() || (isPolicyRequired && !policyCode.trim())}
                                aria-busy={previewLoading}
                            >
                                {previewLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" aria-hidden />
                                        {tb('previewing')}
                                    </>
                                ) : (
                                    <>
                                        <FiSearch size={16} className="me-2" aria-hidden />
                                        {tb('modes.' + modeKey + '.previewButton')}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                    <p id={fieldIdPrefix + '-form-hint'} className="cfbd-form-hint">
                        {isPolicyRequired
                            ? tb('modes.byDatePolicy.formHint')
                            : tb('tripDayHint')}
                    </p>
                </Form>
            </section>

            {previewError && (
                <Alert variant="danger" role="alert" dismissible onClose={() => setPreviewError(null)}>
                    {previewError}
                </Alert>
            )}

            {deleteError && (
                <Alert variant="danger" role="alert" dismissible onClose={() => setDeleteError(null)}>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <span>{deleteError}</span>
                        {previewHasData && (
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={handleOpenConfirm}
                                disabled={isBusy}
                                aria-label={tb('retryDelete')}
                            >
                                <FiRefreshCw size={14} className="me-1" aria-hidden />
                                {tb('retryDelete')}
                            </Button>
                        )}
                    </div>
                </Alert>
            )}

            {/* Section 3 — Preview */}
            {(previewLoading || previewReady) && (
                <section className="cfbd-section cfbd-section--preview" aria-labelledby="cfbd-section-3-title">
                    <Card className="cfbd-preview-card">
                        <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                            <h4 id="cfbd-section-3-title" className="cfbd-section__title mb-0">
                                {tb('previewTitle')}
                            </h4>
                            {previewReady && (
                                <div className="d-flex flex-wrap gap-2">
                                    <Badge bg="secondary">{previewTripDay}</Badge>
                                    {previewedPolicyCode ? (
                                        <Badge bg="info" text="dark">{previewedPolicyCode}</Badge>
                                    ) : !isPolicyRequired && (
                                        <Badge bg="light" text="dark">{tb('policyCodeAll')}</Badge>
                                    )}
                                    {previewEmpty && (
                                        <Badge bg="success">{tb('noDataBadge')}</Badge>
                                    )}
                                    {previewHasData && (
                                        <Badge bg="warning" text="dark">
                                            {tb('confirmationRequiredBadge')}
                                        </Badge>
                                    )}
                                </div>
                            )}
                        </Card.Header>
                        <Card.Body>
                            {previewLoading && (
                                <div role="status" aria-live="polite" aria-busy="true">
                                    <span className="visually-hidden">{tb('previewing')}</span>
                                    <PreviewSkeleton />
                                </div>
                            )}

                            {previewReady && (
                                <>
                                    {preview?.message && (
                                        <p className="text-muted mb-3">{preview.message}</p>
                                    )}

                                    {previewHasData && (
                                        <>
                                            <p className="cfbd-subsection-label">{tb('subsectionDataToDelete')}</p>
                                            <Row className="g-3 mb-4">
                                                {getPreviewCounts(preview).map(({ key, value }) => (
                                                    <CountCard
                                                        key={key}
                                                        label={tb('counts.' + key)}
                                                        value={value}
                                                    />
                                                ))}
                                            </Row>

                                            <div className="cfbd-impact-total" role="status">
                                                <span className="cfbd-impact-total__label">
                                                    {tb('counts.total_records_to_delete')}
                                                </span>
                                                <span className="cfbd-impact-total__value">
                                                    {getPreviewTotal(preview)}
                                                </span>
                                            </div>

                                            <div className="cfbd-timeline-block mt-4">
                                                <p className="cfbd-subsection-label">{tb('timeline.title')}</p>
                                                <DeletionTimeline
                                                    items={getTimelineCounts(preview, 'preview')}
                                                    tb={tb}
                                                    mode="preview"
                                                />
                                            </div>
                                        </>
                                    )}

                                    {previewEmpty && (
                                        <div className="cfbd-empty" role="status">
                                            <FiInfo size={40} className="cfbd-empty__icon" aria-hidden />
                                            <h5 className="cfbd-empty__title">{tb('emptyState.title')}</h5>
                                            <p className="cfbd-empty__message">{tb('emptyState.message')}</p>
                                            <Button
                                                variant="outline-primary"
                                                className="cfbd-btn-full-mobile"
                                                onClick={focusDateInput}
                                            >
                                                {tb('emptyState.changeDate')}
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </section>
            )}

            {/* Section 4 — Zone destructive */}
            {previewHasData && (
                <section className="cfbd-section cfbd-section--danger" aria-labelledby="cfbd-section-4-title">
                    <h4 id="cfbd-section-4-title" className="visually-hidden">{tb('sectionDangerTitle')}</h4>
                    <Alert variant="danger" className="cfbd-danger-alert" role="alert">
                        <div className="cfbd-danger-alert__header">
                            <FiAlertTriangle size={22} className="cfbd-danger-alert__icon" aria-hidden />
                            <strong>{tb('dangerAlert.title')}</strong>
                        </div>
                        <p className="mb-1">{tb('dangerAlert.line1')}</p>
                        <p className="mb-0 fw-semibold">{tb('dangerAlert.line2')}</p>
                    </Alert>
                    <Button
                        variant="danger"
                        className="cfbd-btn-danger cfbd-btn-full-mobile"
                        onClick={handleOpenConfirm}
                        disabled={isBusy}
                        aria-label={tb('deleteButtonAria')}
                    >
                        <FiTrash2 size={16} className="me-2" aria-hidden />
                        {tb('modes.' + modeKey + '.deleteButton')}
                    </Button>
                </section>
            )}

            {/* Résultat succès */}
            {deleteResult?.success && (
                <section className="cfbd-section cfbd-section--success" aria-labelledby="cfbd-success-title">
                    <Alert variant="success" role="status">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <FiCheckCircle aria-hidden />
                            <strong id="cfbd-success-title">
                                {deleteResult.message || tb('successTitle')}
                            </strong>
                        </div>

                        <div className="cfbd-audit-log" aria-labelledby="cfbd-audit-title">
                            <h5 id="cfbd-audit-title" className="cfbd-audit-log__title">{tb('auditLog.title')}</h5>
                            <dl className="cfbd-audit-log__list">
                                <div className="cfbd-audit-log__row">
                                    <dt>{tb('auditLog.user')}</dt>
                                    <dd>{userEmail}</dd>
                                </div>
                                <div className="cfbd-audit-log__row">
                                    <dt>{tb('auditLog.action')}</dt>
                                    <dd>{tb('auditLog.actionValue')}</dd>
                                </div>
                                <div className="cfbd-audit-log__row">
                                    <dt>{tb('auditLog.tripDay')}</dt>
                                    <dd>{deleteResult.trip_day}</dd>
                                </div>
                                <div className="cfbd-audit-log__row">
                                    <dt>{tb('auditLog.policyCode')}</dt>
                                    <dd>{resultPolicyCode || tb('policyCodeAll')}</dd>
                                </div>
                                <div className="cfbd-audit-log__row">
                                    <dt>{tb('auditLog.deletedAt')}</dt>
                                    <dd>{deletedAt ? formatParisDateTime(deletedAt) : '—'}</dd>
                                </div>
                            </dl>
                        </div>

                        <div className="cfbd-timeline-block mt-3">
                            <p className="cfbd-subsection-label">{tb('timeline.resultTitle')}</p>
                            <DeletionTimeline
                                items={getTimelineCounts(deleteResult, 'deleted')}
                                tb={tb}
                                mode="deleted"
                            />
                        </div>

                        <Button
                            variant="outline-success"
                            size="sm"
                            className="mt-3 cfbd-btn-full-mobile"
                            onClick={() => {
                                setDeleteResult(null);
                                setDeletedAt(null);
                                setDeletedPolicyCode('');
                                setPolicyCode('');
                                handleTripDayChange(getParisTodayIso());
                            }}
                        >
                            {tb('analyzeAnother')}
                        </Button>
                    </Alert>
                </section>
            )}

            <Modal
                show={showConfirm}
                onHide={() => !deleteLoading && setShowConfirm(false)}
                backdrop={deleteLoading ? 'static' : true}
                keyboard={!deleteLoading}
                centered
                className="cfbd-confirm-modal"
                aria-labelledby="cfbd-confirm-modal-title"
            >
                <Modal.Header closeButton={!deleteLoading}>
                    <Modal.Title id="cfbd-confirm-modal-title" className="text-danger">
                        <FiAlertTriangle className="me-2" aria-hidden />
                        {tb('confirmTitle')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger" className="py-2 mb-3">
                        <strong>{tb('dangerAlert.title')}</strong>
                        <p className="mb-0 small mt-1">{tb('dangerAlert.line2')}</p>
                    </Alert>
                    <p className="mb-2">{tb('confirmMessage')}</p>
                    <p className="mb-3">
                        <strong>{tb('tripDayLabel')} :</strong> {previewTripDay}
                    </p>
                    <p className="mb-3">
                        <strong>{tb('policyCodeLabel')} :</strong>{' '}
                        {previewedPolicyCode || tb('policyCodeAll')}
                    </p>
                    <p className="mb-3 text-danger fw-semibold">
                        {tb('confirmTotal', { total: previewTotal })}
                    </p>

                    <DeletionTimeline
                        items={getTimelineCounts(preview, 'preview')}
                        tb={tb}
                        mode="preview"
                    />

                    <Form.Check
                        type="checkbox"
                        id="cfbd-confirm-checkbox"
                        className="mt-3 mb-3"
                        checked={confirmChecked}
                        disabled={deleteLoading}
                        onChange={(e) => setConfirmChecked(e.target.checked)}
                        label={tb('confirmCheckbox')}
                    />

                    {isHighRisk && (
                        <Form.Group>
                            <Form.Label htmlFor="cfbd-confirm-text" className="fw-semibold">
                                {tb('confirmTextLabel')}
                            </Form.Label>
                            <Form.Control
                                ref={confirmInputRef}
                                id="cfbd-confirm-text"
                                type="text"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                disabled={deleteLoading}
                                placeholder={tb('confirmTextPlaceholder', { tripDay: previewTripDay })}
                                autoComplete="off"
                                aria-describedby="cfbd-confirm-text-hint"
                                aria-invalid={confirmText.length > 0 && !isConfirmValid}
                            />
                            <Form.Text id="cfbd-confirm-text-hint" className="text-muted">
                                {tb('confirmTextHint', { threshold: 1000 })}
                            </Form.Text>
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer className="cfbd-modal-footer">
                    <Button
                        variant="outline-secondary"
                        className="cfbd-btn-full-mobile"
                        onClick={() => setShowConfirm(false)}
                        disabled={deleteLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="danger"
                        className="cfbd-btn-full-mobile"
                        onClick={handleConfirmDelete}
                        disabled={deleteLoading || !isConfirmValid}
                        aria-label={tb('confirmDeleteAria')}
                    >
                        {deleteLoading ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-2" aria-hidden />
                                {tb('deleting')}
                            </>
                        ) : (
                            tb('confirmDelete')
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CheckForecastBulkDeleteTab;
